import {
  CompleteProfileModal,
  DefaultAvatarIcon,
  LegalAgreementModal,
  Logo,
  SupportModal,
} from '@/components';
import { RoutePath } from '@/config';
import {
  useAuthContext,
  useLegalAgreementGate,
  useLegalMutationsAllowed,
  useProfile,
} from '@/hooks';

import BurgerIcon from '../assets/icons/burger.svg?react';
import CloseXIcon from '../assets/icons/close-x.svg?react';
import CubeOutlineIcon from '../assets/icons/cube-outline.svg?react';
import HeadsetIcon from '../assets/icons/headset.svg?react';
import HistoryOutlineIcon from '../assets/icons/history-outline.svg?react';
import IconArrowWhiteIcon from '../assets/icons/icon-arrow-white.svg?react';
import type { MouseEvent, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ClientInnerPageLayout = ({ children }: PropsWithChildren) => {
  const { isAuthorized, clientNeedsProfileSetup } = useAuthContext();
  const profile = useProfile(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profileSetupDismissed, setProfileSetupDismissed] = useState(false);
  const [legalAgreementDismissed, setLegalAgreementDismissed] = useState(false);
  const { required: legalRequiredByGate, clear: clearLegalRequiredByGate } =
    useLegalAgreementGate();
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();

  const needsLegalAgreement = profile?.legal_agreement_accepted === false;

  useEffect(() => {
    if (!clientNeedsProfileSetup) {
      setProfileSetupDismissed(false);
    }
  }, [clientNeedsProfileSetup]);

  useEffect(() => {
    if (!needsLegalAgreement && !legalRequiredByGate) {
      setLegalAgreementDismissed(false);
    }
  }, [needsLegalAgreement, legalRequiredByGate]);

  // Soft-баннер/модалка (можно закрыть и читать ЛК) + hard после 403.
  const isLegalAgreementModalOpen =
    legalRequiredByGate || (needsLegalAgreement && !legalAgreementDismissed);

  // Согласие важнее заполнения профиля: PUT /client/people под гейтом legal.
  const isProfileSetupModalOpen =
    clientNeedsProfileSetup &&
    !profileSetupDismissed &&
    !isLegalAgreementModalOpen;

  const handleLegalModalClose = () => {
    setLegalAgreementDismissed(true);
    clearLegalRequiredByGate();
  };

  const handleLegalAccepted = () => {
    const wasForcedByGate = legalRequiredByGate;
    clearLegalRequiredByGate();
    setLegalAgreementDismissed(false);
    // Мутации с side effects не ретраим слепо — просим пользователя повторить.
    if (wasForcedByGate) {
      toast.info('Согласие сохранено. Повторите последнее действие.');
    }
  };

  const handleCreateOrderClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!legalMutationsAllowed) {
      event.preventDefault();
      requireConsent();
    }
  };

  if (isAuthorized) {
    sessionStorage.setItem('lastPath', location.pathname);
  }

  const avatarUrl = profile?.avatar;
  const userName = profile?.name ?? 'Пользователь';

  return (
    <div className="min-h-screen bg-[#f1f7fa] md:grid md:h-screen md:min-w-[1280px] md:grid-cols-[260px_1fr] md:gap-2">
      {/* Mobile top bar */}
      <header className="flex items-center justify-between bg-white p-[16px] md:hidden md:bg-none">
        <div className="flex items-center">
          <Logo />
        </div>
        <button
          type="button"
          aria-label="Открыть меню"
          onClick={() => setIsMobileMenuOpen(true)}
          className="flex size-[44px] items-center justify-center rounded-[20px] bg-white"
        >
          <BurgerIcon className="size-[40px]" />
        </button>
      </header>

      {/* Desktop aside */}
      <aside className="relative hidden w-[260px] flex-col items-start justify-between self-stretch bg-white p-5 md:flex">
        <div className="relative flex w-full flex-[0_0_auto] flex-col items-start justify-center gap-6 self-stretch">
          <div className="ml-[16px]">
            <Logo />
          </div>

          <div className="relative flex w-full flex-[0_0_auto] flex-col items-start gap-2 self-stretch">
            <NavLink
              to={RoutePath.ClientCurrentOrder}
              className={({ isActive }) =>
                `flex items-center gap-[0.75rem] px-[1rem] py-[0.625rem] relative self-stretch w-full flex-[0_0_auto] rounded-[12px] hover:bg-[#f1f7fa] ${
                  isActive ? 'bg-[#f1f7fa]' : ''
                }`
              }
            >
              <CubeOutlineIcon className="relative size-5" />
              <div className="relative flex-1 text-[1rem] font-normal leading-6 tracking-normal">
                Текущий заказ
              </div>
            </NavLink>

            <NavLink
              to={RoutePath.ClientOrderHistory}
              className={({ isActive }) =>
                `flex items-center gap-[0.75rem] px-[1rem] py-[0.625rem] relative self-stretch w-full flex-[0_0_auto] hover:bg-[#f1f7fa] rounded-[12px] ${
                  isActive ? 'bg-[#f1f7fa]' : ''
                }`
              }
            >
              <HistoryOutlineIcon className="relative size-5" />
              <div className="relative flex-1 text-[1rem] font-normal leading-6 tracking-normal">
                История заказов
              </div>
            </NavLink>

            <button
              type="button"
              onClick={() => setIsSupportModalOpen(true)}
              className="relative flex w-full flex-[0_0_auto] items-center gap-3 self-stretch rounded-[12px] px-4 py-2.5 text-[#151515] transition-colors hover:bg-[#f1f7fa]"
            >
              <HeadsetIcon className="relative size-5" />
              <div className="relative flex-1 text-left text-[1rem] font-normal leading-6 tracking-normal">
                Поддержка
              </div>
            </button>
          </div>

          <NavLink
            to={RoutePath.ClientCreateOrder}
            onClick={handleCreateOrderClick}
            aria-disabled={!legalMutationsAllowed}
            className={({ isActive }) =>
              `relative flex w-full flex-[0_0_auto] items-center justify-center gap-3 self-stretch rounded-[12px] px-4 py-2.5 transition-colors ${
                !legalMutationsAllowed || isActive
                  ? 'cursor-not-allowed bg-[#f1f7fa] text-[#151515] opacity-50'
                  : 'bg-[#0788f1] text-white hover:bg-[#0670c4]'
              }`
            }
          >
            <div className="relative text-[1rem] font-normal leading-6 tracking-normal">
              Сделать заказ
            </div>
          </NavLink>
        </div>

        <div className="relative flex w-full flex-[0_0_auto] flex-col items-start gap-2 self-stretch">
          <button
            type="button"
            onClick={() => navigate(RoutePath.ClientProfile)}
            className="group relative flex w-full flex-[0_0_auto] items-center gap-3 self-stretch rounded-[12px] bg-[#f1f7fa] p-2.5 transition-colors hover:bg-[#e4f0f7]"
          >
            {avatarUrl ? (
              <div
                className="relative size-10 shrink-0 rounded-[100px] bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${avatarUrl})` }}
              />
            ) : (
              <DefaultAvatarIcon className="size-10" />
            )}
            <div className="relative min-w-0 flex-1 truncate text-left text-[1rem] font-normal leading-6 tracking-normal text-[#151515]">
              {userName || 'Пользователь'}
            </div>
            <div className="relative size-4 shrink-0 transition-transform group-hover:-rotate-90">
              <IconArrowWhiteIcon className="w-[5px]text-[#151515] absolute left-[5px] top-1 h-[10px]" />
            </div>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="mt-[24px] min-h-[calc(100vh-24px)] overflow-y-auto rounded-[16px] bg-[#f1f7fa] px-[16px] pb-[24px] md:mt-0 md:max-w-[calc(100vw-250px)] md:p-9">
        {children}
      </div>

      {/* Mobile drawer (slides from left) */}
      <div
        className={`fixed inset-0 z-50 md:hidden ${
          isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <button
          type="button"
          aria-label="Закрыть меню"
          className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <div
          className={`absolute left-0 top-0 flex size-full flex-col overflow-y-auto overscroll-contain bg-white p-[24px] transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-start justify-between">
            <Logo />
            <button
              type="button"
              aria-label="Закрыть"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex size-[44px] items-center justify-center rounded-[20px] bg-white"
            >
              <CloseXIcon className="size-[40px]" />
            </button>
          </div>

          <div className="mt-[32px] flex flex-col gap-[20px]">
            <NavLink
              to={RoutePath.ClientCurrentOrder}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 p-[16px] pl-0 text-[#151515]"
            >
              <CubeOutlineIcon className="size-[24px]" />
              <div className="text-[24px] font-normal leading-6">
                Текущий заказ
              </div>
            </NavLink>
            <NavLink
              to={RoutePath.ClientOrderHistory}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 p-[16px] pl-0 text-[#151515]"
            >
              <HistoryOutlineIcon className="size-[24px]" />
              <div className="text-[24px] font-normal leading-6">
                История заказов
              </div>
            </NavLink>
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsSupportModalOpen(true);
              }}
              className="flex items-center gap-3 p-[16px] pl-0 text-[#151515]"
            >
              <HeadsetIcon className="size-[24px]" />
              <div className="text-[24px] font-normal leading-6">Поддержка</div>
            </button>

            <NavLink
              to={RoutePath.ClientCreateOrder}
              onClick={(event) => {
                handleCreateOrderClick(event);
                if (legalMutationsAllowed) {
                  setIsMobileMenuOpen(false);
                }
              }}
              aria-disabled={!legalMutationsAllowed}
              className={`mt-[12px] flex h-[60px] w-full items-center justify-center rounded-[12px] text-[16px] font-bold leading-5 ${
                legalMutationsAllowed
                  ? 'bg-[#0788f1] text-white'
                  : 'cursor-not-allowed bg-[#0788f1] text-white opacity-50'
              }`}
            >
              Сделать заказ
            </NavLink>
          </div>

          <div className="mt-auto shrink-0 pt-[24px]">
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate(RoutePath.ClientProfile);
              }}
              className="group relative flex h-[72px] w-full items-center gap-[12px] rounded-[16px] bg-[#f1f7fa] p-[16px]"
            >
              {avatarUrl ? (
                <div
                  className="size-[44px] shrink-0 rounded-[100px] bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${avatarUrl})` }}
                />
              ) : (
                <DefaultAvatarIcon className="size-[44px]" />
              )}
              <div className="min-w-0 flex-1 truncate text-left text-[16px] font-normal leading-6 text-[#151515]">
                {userName || 'Пользователь'}
              </div>
              <div className="relative size-4 shrink-0 transition-transform group-hover:-rotate-90">
                <IconArrowWhiteIcon className="absolute left-[5px] top-1 h-[10px] w-[5px] text-[#151515]" />
              </div>
            </button>
          </div>
        </div>
      </div>

      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
      <LegalAgreementModal
        isOpen={isLegalAgreementModalOpen}
        onClose={handleLegalModalClose}
        onAccepted={handleLegalAccepted}
      />
      <CompleteProfileModal
        isOpen={isProfileSetupModalOpen}
        onClose={() => setProfileSetupDismissed(true)}
      />
    </div>
  );
};
